import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { scrollToHomeSection } from '../utils/scrollToHomeSection';
import { onScroll } from '../utils/onScroll';
import './Navigation.css';

const Navigation = ({ content, products = [] }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAppsOpen, setIsAppsOpen] = useState(false);
  const [menuCanAnimate, setMenuCanAnimate] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const appLinks = content.appLinks || [];

  const getProductForAppLink = (href) => {
    const match = href.match(/^\/apps\/([^/]+)$/);
    if (!match) return null;
    return products.find((item) => item.id === match[1]) || null;
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsAppsOpen(false);
  };

  useEffect(() => {
    return onScroll(() => {
      setIsScrolled((wasScrolled) => {
        const nowScrolled = window.scrollY > 50;
        return wasScrolled === nowScrolled ? wasScrolled : nowScrolled;
      });
    });
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
        setIsAppsOpen(false);
        setMenuCanAnimate(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scrollToSection = (sectionId) => {
    scrollToHomeSection(sectionId);
    closeMobileMenu();
  };

  const handleNavClick = (href) => {
    if (href.startsWith('/')) {
      navigate(href);
      closeMobileMenu();
      return;
    }

    const sectionId = href.startsWith('#') ? href.substring(1) : href;
    if (location.pathname === '/') {
      scrollToSection(sectionId);
      return;
    }

    navigate(`/#${sectionId}`);
    closeMobileMenu();
  };

  const handleLogoClick = () => {
    if (location.pathname === '/') {
      scrollToSection('welcome');
      return;
    }
    navigate('/');
  };

  const renderNavItem = (link, className) => {
    const isThemed = Boolean(link.accentColor);
    const resolvedClassName = isThemed
      ? `${className} nav-link--themed`.trim()
      : `${className} nav-link--neutral`.trim();
    const style = isThemed ? { '--nav-accent': link.accentColor } : undefined;

    if (link.href.startsWith('/')) {
      return (
        <Link
          key={link.href}
          to={link.href}
          className={resolvedClassName}
          style={style}
          onClick={() => {
            closeMobileMenu();
          }}
        >
          {link.text}
        </Link>
      );
    }

    return (
      <button
        key={link.href}
        type="button"
        className={resolvedClassName}
        style={style}
        onClick={() => handleNavClick(link.href)}
      >
        {link.text}
      </button>
    );
  };

  return (
    <nav className={`navigation ${isScrolled ? 'scrolled' : ''}`}>
      <div className="nav-container">
        <div className="nav-logo" onClick={handleLogoClick}>
          <img
            src="/images/logo-mark.webp"
            alt="Freedman Labs Logo"
            className="logo-image"
            width={210}
            height={256}
            decoding="async"
          />
          <div className="logo-text">
            <span>{content.logoText}</span>
          </div>
        </div>

        <div className={`nav-menu ${isMobileMenuOpen ? 'active' : ''} ${menuCanAnimate ? 'menu-can-animate' : ''}`}>
          {content.links
            .filter((link) => link.text === 'HOME' || link.text === 'ABOUT')
            .map((link) => renderNavItem(link, 'nav-link'))}

          <div className="nav-apps-desktop">
            {appLinks.map((link) => renderNavItem(
              { ...link, text: link.text.toUpperCase() },
              'nav-link nav-app-link'
            ))}
          </div>

          <div className="nav-apps-mobile">
            <button
              type="button"
              className={`nav-link nav-link--neutral nav-apps-toggle ${isAppsOpen ? 'open' : ''}`}
              onClick={() => setIsAppsOpen(!isAppsOpen)}
              aria-expanded={isAppsOpen}
            >
              APPS
            </button>
            {isAppsOpen && (
              <div className="nav-apps-dropdown">
                {appLinks.map((link) => {
                  const product = getProductForAppLink(link.href);

                  return (
                    <Link
                      key={link.href}
                      to={link.href}
                      className="nav-app-sublink nav-link--themed"
                      style={{ '--nav-accent': link.accentColor }}
                      onClick={closeMobileMenu}
                    >
                      {product && (
                        <img
                          src={product.app.image}
                          alt={product.app.imageAlt}
                          className="nav-app-sublink-icon"
                          width={44}
                          height={44}
                          loading="lazy"
                          decoding="async"
                        />
                      )}
                      <span>{link.text}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          {content.links
            .filter((link) => link.text === 'CONTACT')
            .map((link) => renderNavItem(link, 'nav-link'))}
        </div>

        <button
          type="button"
          className={`hamburger ${isMobileMenuOpen ? 'active' : ''}`}
          onClick={() => {
            setMenuCanAnimate(true);
            setIsMobileMenuOpen(!isMobileMenuOpen);
          }}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </nav>
  );
};

export default Navigation;
